import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';
import { getIconByName } from '../../utils/iconMapping';
import { AnimatedToggle } from '../shared/AnimatedToggle';
import {
  Bars3Icon,
  ArrowUpIcon,
  ArrowDownIcon,
  PencilIcon,
  TrashIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  EllipsisHorizontalIcon
} from '@heroicons/react/24/outline';
import { useRoleSwitcher } from '../../features/auth/hooks/useRoleSwitcher';
import useParentStore from '../../stores/parentStore';
import { calculateVisibleNavItems } from './navMenuLayout';
import {
  filterParentNavigationItems,
  resolveParentNavigationHref,
} from './parentNavigation';

/**
 * MenuItemList - Renders a list of menu items with admin controls and drag-and-drop
 */
export const MenuItemList = ({
  menuItems,
  onToggleVisibility,
  onMoveItem,
  onEditItem,
  onDeleteItem,
  onRoleToggle,
  onReorder,
  onMakeChild,
  showAdminControls = false
}) => {
  const [activeId, setActiveId] = React.useState(null);
  const [draggedItem, setDraggedItem] = React.useState(null);
  const [expandedItems, setExpandedItems] = React.useState(new Set());

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleToggleExpand = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  if (menuItems.length === 0) {
    return (
      <div className="text-center py-8">
        <Bars3Icon className="w-12 h-12 mx-auto text-base-content/30 mb-2" />
        <p className="text-base-content/70">No menu items configured</p>
      </div>
    );
  }

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    const item = menuItems.find(item => item.id === event.active.id);
    setDraggedItem(item);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = menuItems.findIndex(item => item.id === active.id);
      const newIndex = menuItems.findIndex(item => item.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedItems = arrayMove(menuItems, oldIndex, newIndex);
        onReorder?.(reorderedItems);
      }
    }

    setActiveId(null);
    setDraggedItem(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext 
        items={menuItems.map(item => item.id)} 
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {menuItems.map((item, index) => (
            <SortableMenuItemCard
              key={`menu-item-${item.id}`}
              item={item}
              index={index}
              totalItems={menuItems.length}
              onToggleVisibility={onToggleVisibility}
              onMoveItem={onMoveItem}
              onEditItem={onEditItem}
              onDeleteItem={onDeleteItem}
              onRoleToggle={onRoleToggle}
              onMakeChild={onMakeChild}
              showAdminControls={showAdminControls}
              isDragging={activeId === item.id}
              isExpanded={expandedItems.has(item.id)}
              onToggleExpand={handleToggleExpand}
              expandedItems={expandedItems}
            />
          ))}
        </div>
      </SortableContext>
      
      <DragOverlay>
        {activeId && draggedItem ? (
          <MenuItemCard
            item={draggedItem}
            isDragOverlay
            showAdminControls={showAdminControls}
            isExpanded={expandedItems.has(draggedItem.id)}
            onToggleExpand={handleToggleExpand}
            expandedItems={expandedItems}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

/**
 * SortableMenuItemCard - Sortable wrapper for MenuItemCard
 */
export const SortableMenuItemCard = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props.item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <MenuItemCard
        {...props}
        dragAttributes={attributes}
        dragListeners={listeners}
        isDragging={isDragging}
      />
    </div>
  );
};

/**
 * MenuItemCard - Individual menu item display with controls and hierarchical support
 */
export const MenuItemCard = ({
  item,
  index,
  totalItems,
  onToggleVisibility,
  onMoveItem,
  onEditItem,
  onDeleteItem,
  onRoleToggle,
  onMakeChild,
  showAdminControls,
  dragAttributes,
  dragListeners,
  isDragging,
  isDragOverlay = false,
  isExpanded = false,
  onToggleExpand,
  expandedItems
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const depth = item.depth || 0;
  const indentSize = depth * 24; // 24px per level

  return (
    <div
      className={`border rounded-lg bg-base-50 ${
        !item.visible ? 'opacity-60' : ''
      } ${isDragging ? 'shadow-lg' : 'hover:shadow-md'} transition-shadow ${
        showAdminControls && !isDragOverlay ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
      style={{ marginLeft: `${indentSize}px` }}
      {...(showAdminControls && !isDragOverlay ? dragAttributes : {})}
      {...(showAdminControls && !isDragOverlay ? dragListeners : {})}
    >
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Expand/Collapse for parent items */}
            {hasChildren && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onToggleExpand) {
                    onToggleExpand(item.id);
                  }
                }}
                className="btn btn-circle btn-xs btn-ghost"
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDownIcon className="w-3 h-3" />
                ) : (
                  <ChevronRightIcon className="w-3 h-3" />
                )}
              </button>
            )}

            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                {getIconByName(item.icon, "w-5 h-5 text-primary")}
                <span className="font-medium">{item.label}</span>
                {depth > 0 && (
                  <span className="text-xs bg-base-300 px-1 py-0.5 rounded">
                    L{depth}
                  </span>
                )}
              </div>
              <span className="text-xs bg-base-200 px-2 py-1 rounded">
                {item.href}
              </span>
            </div>

            {showAdminControls && (
              <div className="ml-4">
                <RoleToggles
                  roles={item.roles}
                  onRoleToggle={(role) => onRoleToggle?.(item.id, role)}
                />
              </div>
            )}
          </div>

          {showAdminControls && !isDragOverlay && (
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-base-content/60">Visible:</span>
                <AnimatedToggle
                  checked={item.visible}
                  onChange={() => onToggleVisibility?.(item.id)}
                  size="sm"
                  title={item.visible ? 'Hide item' : 'Show item'}
                />
              </div>

              <button
                onClick={() => onMakeChild?.(item.id)}
                className="btn btn-circle btn-sm btn-ghost"
                title="Make child of item above"
                disabled={index === 0 || depth >= 2}
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
              
              <button
                onClick={() => onMoveItem?.(item.id, 'up')}
                disabled={index === 0}
                className="btn btn-circle btn-sm btn-ghost"
                title="Move up"
              >
                <ArrowUpIcon className="w-4 h-4" />
              </button>
              
              <button
                onClick={() => onMoveItem?.(item.id, 'down')}
                disabled={index === totalItems - 1}
                className="btn btn-circle btn-sm btn-ghost"
                title="Move down"
              >
                <ArrowDownIcon className="w-4 h-4" />
              </button>
              
              <button
                onClick={() => onEditItem?.(item)}
                className="btn btn-circle btn-sm btn-info"
                title="Edit item"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              
              <button
                onClick={() => onDeleteItem?.(item.id)}
                className="btn btn-circle btn-sm btn-error"
                title="Delete item"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Children (when expanded) */}
      {hasChildren && isExpanded && (
        <div className="border-t bg-base-100/50">
          {item.children.map((child, childIndex) => (
            <MenuItemCard
              key={`child-${child.id}`}
              item={child}
              index={childIndex}
              totalItems={item.children.length}
              onToggleVisibility={onToggleVisibility}
              onMoveItem={onMoveItem}
              onEditItem={onEditItem}
              onDeleteItem={onDeleteItem}
              onRoleToggle={onRoleToggle}
              onMakeChild={onMakeChild}
              showAdminControls={showAdminControls}
              isExpanded={expandedItems?.has(child.id)}
              onToggleExpand={onToggleExpand}
              expandedItems={expandedItems}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * RoleToggles - Role permission toggles for menu items
 */
export const RoleToggles = ({ roles, onRoleToggle }) => {
  return (
    <div className="flex space-x-1">
      {Object.entries(roles).map(([role, enabled]) => (
        <button
          key={role}
          onClick={() => onRoleToggle?.(role)}
          className={`text-xs px-2 py-1 rounded transition-colors ${
            enabled
              ? 'bg-success text-success-content hover:bg-success/80'
              : 'bg-base-300 text-base-content/50 hover:bg-base-200'
          }`}
          title={`Click to ${enabled ? 'hide from' : 'show to'} ${role}s`}
        >
          {role}
        </button>
      ))}
    </div>
  );
};

/**
 * SimpleMenuList - Basic menu list for navigation with hierarchical support
 * Uses React Router <Link> for SPA navigation (no full page reloads)
 */
export const SimpleMenuList = ({
  menuItems,
  className = "",
  itemClassName = "",
  renderItem
}) => {
  const defaultRenderItem = (item) => (
    <Link
      to={item.href}
      data-tour-route={item.href}
      className={`flex items-center gap-3 ${itemClassName}`}
    >
      {getIconByName(item.icon, "w-5 h-5")}
      {item.label}
    </Link>
  );

  const renderMenuItems = (items, isChild = false) => {
    return items.map((item) => {
      const hasChildren = item.children && item.children.length > 0;

      if (hasChildren) {
        return (
          <li key={`simple-menu-${item.id}`}>
            <details>
              <summary className={`flex items-center gap-3 ${itemClassName} ${isChild ? 'pl-4' : ''}`}>
                {getIconByName(item.icon, "w-5 h-5")}
                {item.label}
              </summary>
              <ul className="ml-4">
                {renderMenuItems(item.children, true)}
              </ul>
            </details>
          </li>
        );
      }

      return (
        <li key={`simple-menu-${item.id}`}>
          <div className={isChild ? 'pl-4' : ''}>
            {renderItem ? renderItem(item) : defaultRenderItem(item)}
          </div>
        </li>
      );
    });
  };

  return (
    <ul className={`menu ${className}`}>
      {renderMenuItems(menuItems)}
    </ul>
  );
};

/**
 * NavMenuItems - Navbar menu items with "More" overflow dropdown.
 * Shows up to MAX_VISIBLE items; the rest go into a "More" menu.
 */
const MAX_VISIBLE = 8;
const NAV_ITEM_GAP = 8;

export const NavMenuItems = ({ menuItems, className = "" }) => {
  const { currentRole } = useRoleSwitcher();
  const { linkedChildren, hasLoadedChildren } = useParentStore();
  const location = useLocation();
  const containerRef = React.useRef(null);
  const measurementRef = React.useRef(null);
  const [visibleCount, setVisibleCount] = React.useState(MAX_VISIBLE);
  const [openDropdownId, setOpenDropdownId] = React.useState(null);

  const safeMenuItems = Array.isArray(menuItems) ? menuItems : [];
  const availableMenuItems = filterParentNavigationItems(safeMenuItems, {
    currentRole,
    linkedChildren,
  });

  // Top navbar = workflows only: QI, Q, Tasks, Messages, Dashboard
  // (per docs/MENU_IA_DIRECTION.md). Everything else goes to:
  //   - right rail (substrate)
  //   - avatar menu (identity)
  //   - inside relevant domain (incidental — stickers, awards)
  //   - inside People (welcome letters)
  //   - retired (setup hub, admin tools)
  // Smart Lists and its Lists wrapper are active workflows again (SWA-044).
  const NAVBAR_HIDE_IDS = new Set([
    // Substrate → right rail
    'organization', 'org-chart',
    'domains',
    'timetable', 'timetable-builder', 'timetable-assignments',
    'people',
    'calendar',
    'workflow-templates',
    'subjects', 'grades',
    // Identity → avatar menu
    'settings', 'school-profile', 'access-id', 'pricing', 'billing',
    // Inside relevant domain
    'stickers', 'awards',
    // Inside People (per-person action) and Messages (send access code)
    'welcome-letters',
    'access-codes', 'access-id', 'parent-access-codes', 'accessId',
    // Retired / parked
    'setup',
    'seating-plans',
    'users',
    'admin-tools',
    'messaging-preferences',
    'messaging-test',
    // "School" wrapper (Staff / Classes / Students / Offences / Date Ranges)
    // — its children belong in People, Calendar, or inside the relevant domain.
    'school_1757962663554',
  ]);
  const NAVBAR_HIDE_LABELS = new Set([
    'Organization', 'Org Chart',
    'Domains',
    'Timetable', 'Timetable Builder', 'Assign Timetable',
    'People',
    'Calendar',
    'Workflow Templates', 'Workflow templates',
    'Subjects', 'Grades',
    'Settings', 'School Profile', 'Access ID', 'My ID', 'Pricing', 'Billing',
    'Stickers', 'Awards',
    'Welcome Letters',
    'Access codes', 'Access Codes', 'Parent Access Codes', 'Access ID',
    'Setup',
    'ClassList Pro',
    'Seating Plans',
    'Users',
    'Admin Tools',
    'Test Messages',
    'School', // the dropdown wrapper, not the school chip in the avatar menu
  ]);

  const topLevelItems = availableMenuItems.filter(item =>
    (item.depth === 0 || item.depth === undefined) && !item.parentId &&
    !NAVBAR_HIDE_IDS.has(item.id) &&
    !NAVBAR_HIDE_LABELS.has(item.label)
  );

  const substituteRouteParams = (href) => {
    return resolveParentNavigationHref(href, {
      currentRole,
      linkedChildren,
      hasLoadedChildren,
    });
  };

  const isRouteActive = (href) => {
    if (!href) return false;
    const resolved = substituteRouteParams(href);
    if (resolved === '/') return location.pathname === '/';
    return location.pathname === resolved || location.pathname.startsWith(resolved + '/');
  };

  // Tab-style menu button. Fills full navbar height (h-20) so the hover area is
  // continuous with the dropdown below — no gap of death for dropdown-hover.
  const stackButtonClass = "group relative flex flex-col items-center justify-center min-w-[72px] h-20 px-2 cursor-pointer focus:outline-none";

  const renderStackContent = (item, active) => (
    <div
      className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors duration-200 ${
        active ? 'bg-primary/10' : 'group-hover:bg-base-200/60'
      }`}
    >
      {getIconByName(
        item.icon,
        `w-5 h-5 shrink-0 transition-all duration-200 group-hover:-translate-y-0.5 ${
          active ? 'text-primary' : 'text-base-content/60 group-hover:text-base-content'
        }`
      )}
      <span
        className={`text-[11px] leading-none whitespace-nowrap transition-colors duration-200 ${
          active ? 'text-primary font-semibold' : 'font-medium text-base-content/70 group-hover:text-base-content'
        }`}
      >
        {item.label}
      </span>
      <span
        aria-hidden="true"
        className={`mt-0.5 h-[2px] w-6 rounded-full bg-primary transition-opacity duration-200 ${
          active ? 'opacity-100' : 'opacity-0'
        }`}
      />
    </div>
  );

  const itemLayoutKey = topLevelItems.map(item => `${item.id}:${item.label}`).join('|');

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    const measurement = measurementRef.current;
    if (!container || !measurement) return undefined;

    const updateVisibleCount = () => {
      const itemWidths = Array.from(measurement.querySelectorAll('[data-nav-measure-item]'))
        .map(element => element.getBoundingClientRect().width);
      const moreWidth = measurement.querySelector('[data-nav-measure-more]')?.getBoundingClientRect().width || 72;

      setVisibleCount(calculateVisibleNavItems({
        itemWidths,
        availableWidth: container.getBoundingClientRect().width,
        moreWidth,
        maxVisible: MAX_VISIBLE,
        gap: NAV_ITEM_GAP,
      }));
    };

    updateVisibleCount();
    const resizeObserver = new ResizeObserver(updateVisibleCount);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [itemLayoutKey]);

  const visibleItems = topLevelItems.slice(0, visibleCount);
  const overflowItems = topLevelItems.slice(visibleCount);

  const dropdownInteractionProps = (dropdownId) => ({
    onMouseEnter: () => setOpenDropdownId(dropdownId),
    onMouseLeave: (event) => {
      if (!event.currentTarget.contains(document.activeElement)) {
        setOpenDropdownId(current => current === dropdownId ? null : current);
      }
    },
    onFocusCapture: () => setOpenDropdownId(dropdownId),
    onBlurCapture: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setOpenDropdownId(current => current === dropdownId ? null : current);
      }
    },
  });

  const renderMenuItem = (item) => {
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      const active = item.children.some(c => isRouteActive(c.href));
      const triggerRoute = item.children[0]?.href;
      return (
        <div
          className={`dropdown ${openDropdownId === item.id ? 'dropdown-open' : ''}`}
          key={`nav-${item.id}`}
          {...dropdownInteractionProps(item.id)}
        >
          <div
            tabIndex={0}
            role="button"
            data-tour-route={triggerRoute}
            className={stackButtonClass}
          >
            {renderStackContent(item, active)}
          </div>
          {openDropdownId === item.id && (
            <ul
              tabIndex={0}
              className="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow-lg border border-base-300"
            >
              {item.children.map((child) => (
                <li key={`nav-child-${child.id}`}>
                  <Link
                    to={substituteRouteParams(child.href)}
                    data-tour-route={child.href}
                    className="flex items-center gap-2"
                  >
                    {getIconByName(child.icon, "w-4 h-4")}
                    {child.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    const active = isRouteActive(item.href);
    return (
      <Link
        key={`nav-${item.id}`}
      to={substituteRouteParams(item.href)}
      data-tour-route={item.href}
      className={stackButtonClass}
      onMouseEnter={() => setOpenDropdownId(null)}
    >
        {renderStackContent(item, active)}
      </Link>
    );
  };

  const renderOverflowItem = (item) => {
    const hasChildren = item.children && item.children.length > 0;

    if (hasChildren) {
      return (
        <li key={`more-${item.id}`}>
          <details>
            <summary className="flex items-center gap-2">
              {getIconByName(item.icon, "w-4 h-4")}
              {item.label}
            </summary>
            <ul>
              {item.children.map((child) => (
                <li key={`more-child-${child.id}`}>
                  <Link
                    to={substituteRouteParams(child.href)}
                    data-tour-route={child.href}
                    className="flex items-center gap-2"
                  >
                    {getIconByName(child.icon, "w-4 h-4")}
                    {child.label}
                  </Link>
                </li>
              ))}
            </ul>
          </details>
        </li>
      );
    }

    return (
      <li key={`more-${item.id}`}>
        <Link
          to={substituteRouteParams(item.href)}
          data-tour-route={item.href}
          className="flex items-center gap-2"
        >
          {getIconByName(item.icon, "w-4 h-4")}
          {item.label}
        </Link>
      </li>
    );
  };

  return (
    <div ref={containerRef} className={`relative w-full min-w-0 ${className}`}>
      <div className="flex items-center justify-center gap-2">
        {visibleItems.map(renderMenuItem)}
        {overflowItems.length > 0 && (
          <div
            className={`dropdown ${openDropdownId === '__more__' ? 'dropdown-open' : ''}`}
            {...dropdownInteractionProps('__more__')}
          >
            <div tabIndex={0} role="button" className={stackButtonClass}>
              <div className="flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors duration-200 group-hover:bg-base-200/60">
                <EllipsisHorizontalIcon className="w-5 h-5 shrink-0 text-base-content/60 group-hover:text-base-content transition-all duration-200 group-hover:-translate-y-0.5" />
                <span className="text-[11px] font-medium leading-none text-base-content/70 group-hover:text-base-content transition-colors duration-200">
                  More
                </span>
                <span aria-hidden="true" className="mt-0.5 h-[2px] w-6 rounded-full bg-primary opacity-0" />
              </div>
            </div>
            {openDropdownId === '__more__' && (
              <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow-lg border border-base-300"
              >
                {overflowItems.map(renderOverflowItem)}
              </ul>
            )}
          </div>
        )}
      </div>

      <div
        ref={measurementRef}
        aria-hidden="true"
        className="invisible pointer-events-none absolute left-0 top-0 flex w-max items-center gap-2"
      >
        {topLevelItems.slice(0, MAX_VISIBLE).map(item => (
          <div key={`measure-${item.id}`} data-nav-measure-item className={stackButtonClass}>
            {renderStackContent(item, false)}
          </div>
        ))}
        <div data-nav-measure-more className={stackButtonClass}>
          <div className="flex flex-col items-center gap-1 px-3 py-2 rounded-lg">
            <EllipsisHorizontalIcon className="h-5 w-5 shrink-0" />
            <span className="text-[11px] font-medium leading-none">More</span>
            <span aria-hidden="true" className="mt-0.5 h-[2px] w-6" />
          </div>
        </div>
      </div>
    </div>
  );
};
